import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, TextField, IconButton, Typography, useTheme } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/**
 * Description field with a suggestion dropdown behind the bulb icon.
 * Shared by AddTimeEntryForm, EditTimeEntryForm, QuickTimeEntryForm and Timer so the
 * bulb behaves the same way in every form.
 *
 * The list has two sections, in this order: the member's recently used descriptions
 * (`suggestions`) and then the teamspace's tag names (`tagSuggestions`). Either may be
 * empty, and an empty section is omitted rather than shown with no rows.
 *
 * It is a type-ahead: whatever is in the field filters both sections by prefix, so typing
 * "l" narrows the list to descriptions starting with "l". The bulb is drawn whenever there
 * is anything to suggest and is lit (`info.main`) only while something still matches, so a
 * dim bulb means "nothing starts with what you have typed" rather than "no suggestions".
 *
 * The list opens on its own whenever the focused field has matches — the bulb toggles it,
 * it is not the only way in. Picking a row or pressing Escape dismisses the list without
 * dropping focus; typing again brings it back.
 *
 * The dropdown is rendered by us rather than by a native <datalist>: a programmatic
 * input.click() does not open a datalist picker, so the bulb used to do nothing in the
 * forms that relied on one.
 *
 * `typeToOpen` (all four forms) stops focus alone from opening the list: Add, Edit and
 * Quick Create autofocus the description, so the default mode dropped the list over the
 * dialog the moment it appeared. The list opens when the member types something with a
 * match, and closes again while nothing matches or the field is empty. The bulb also opens
 * it, showing every row when the field is empty. The bulb is dim by default and lit only
 * while the list is showing. A pick, Escape, blur or a bulb click closes the list; typing
 * again reopens it.
 *
 * Keyboard, in every mode: Down and Up open the list and move through its rows across both
 * sections, wrapping at the ends. Enter picks the highlighted row and goes no further, so it
 * cannot submit the form; with nothing highlighted, Enter reaches the caller as usual. Escape
 * on an open list only closes the list; a second Escape reaches the caller. Keys pressed while
 * an IME is composing belong to the IME and are ignored here.
 *
 * Props beyond the ones listed are forwarded to the underlying TextField.
 */
const DescriptionSuggestField = ({
  typeToOpen = false,
  suggestions = [],
  tagSuggestions = [],
  onSelect,
  containerSx,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  InputProps,
  inputProps,
  value,
  ...textFieldProps
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const activeRowRef = useRef(null);
  const listId = useId();
  // Position in `options`, the rows of both sections in display order. -1 means no row.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  // Dismissing is remembered separately from focus so that picking a row, or pressing
  // Escape, does not immediately re-open the list under the cursor. Typing or re-focusing
  // clears it, because at that point the member is asking for suggestions again.
  const [dismissed, setDismissed] = useState(false);
  // typeToOpen: whether the bulb has the list open. Nothing but the bulb sets it.
  const [bulbOpen, setBulbOpen] = useState(false);
  // typeToOpen: the member has typed since the list last closed.
  const [typing, setTyping] = useState(false);

  // Content-aware: once anything is typed, only offer completions of it. Prefix match,
  // not substring — the list is a type-ahead, so the row has to continue what is in the
  // field for picking it to leave the typed text intact.
  const query = String(value == null ? '' : value).trim().toLowerCase();

  // Recently used comes first: a description the member actually wrote is a better guess
  // than a bare tag name. A name that is in both sections is therefore shown once, under
  // Recently used — the same string either way, and two identical rows read as a bug.
  const sections = useMemo(() => {
    const seen = new Set();
    const pick = (items) =>
      (items || []).filter((item) => {
        const key = String(item == null ? '' : item).trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        if (query && !key.startsWith(query)) return false;
        seen.add(key);
        return true;
      });
    return [
      { caption: 'Recently used', items: pick(suggestions) },
      { caption: 'Tags', items: pick(tagSuggestions) },
    ].filter((section) => section.items.length > 0);
  }, [suggestions, tagSuggestions, query]);

  // Two different questions: whether to draw the bulb at all, and whether to light it.
  const hasAnySuggestions = suggestions.length > 0 || tagSuggestions.length > 0;
  const hasMatches = sections.length > 0;

  // Default mode: the list opens by itself — focus the field and any matches are already
  // on screen. typeToOpen: while the bulb holds it open, or while typed text has matches.
  // Checking `query` here, not just `typing`, keeps the list shut when a form clears the
  // field while it still has focus ("Save and add another").
  const open = typeToOpen
    ? focused && (bulbOpen || (typing && Boolean(query) && hasMatches))
    : focused && hasMatches && !dismissed;
  // typeToOpen lights the bulb for "list is open"; the default mode lights it for "something matches".
  const lit = typeToOpen ? open : hasMatches;

  const closeTypeToOpenList = () => {
    setBulbOpen(false);
    setTyping(false);
  };

  const options = sections.flatMap((section) => section.items);
  // Compared by content, so a new array holding the same rows keeps the highlight in place.
  const optionsKey = options.join('\n');
  const optionId = (index) => `${listId}-option-${index}`;

  useEffect(() => {
    setActiveIndex(-1);
  }, [optionsKey]);

  // On close only: Down and Up open the list and set the highlight in one step.
  useEffect(() => {
    if (!open) setActiveIndex(-1);
  }, [open]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const pickOption = (option) => {
    onSelect?.(option);
    setDismissed(true);
    closeTypeToOpenList();
  };

  const moveHighlight = (key) => {
    const step = key === 'ArrowDown' ? 1 : -1;
    if (!open) {
      setDismissed(false);
      setBulbOpen(true);
      setActiveIndex(step === 1 ? 0 : options.length - 1);
      return;
    }
    setActiveIndex((current) => {
      if (current === -1) return step === 1 ? 0 : options.length - 1;
      return (current + step + options.length) % options.length;
    });
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && options.length > 0) {
      e.preventDefault();
      moveHighlight(e.key);
      return;
    }
    if (e.key === 'Enter' && open && activeIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      pickOption(options[activeIndex]);
      return;
    }
    if (e.key === 'Escape' && open) {
      // Swallow it: the list is what the member is dismissing, not the dialog.
      e.stopPropagation();
      setDismissed(true);
      closeTypeToOpenList();
      return;
    }
    onKeyDown?.(e);
  };

  // Running position across sections, so each row knows its index in `options`.
  let rowIndex = -1;

  return (
    <Box sx={{ position: 'relative', ...containerSx }}>
      <TextField
        label="Description (optional)"
        type="text"
        name="description"
        inputRef={inputRef}
        value={value}
        onFocus={(e) => {
          setFocused(true);
          setDismissed(false);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          // Delay so a mousedown on a suggestion still lands before the list unmounts.
          setTimeout(() => {
            setFocused(false);
            closeTypeToOpenList();
          }, 150);
          onBlur?.(e);
        }}
        onChange={(e) => {
          // Typing after a pick means "show me the list again", now filtered by what is typed.
          setDismissed(false);
          setTyping(true);
          // Even if the same rows still match: Enter must not swap new typing for an older choice.
          setActiveIndex(-1);
          onChange?.(e);
        }}
        onKeyDown={handleKeyDown}
        {...textFieldProps}
        inputProps={{
          ...inputProps,
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': open,
          'aria-controls': open ? listId : undefined,
          'aria-activedescendant': open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
        }}
        InputProps={{
          ...InputProps,
          endAdornment: hasAnySuggestions ? (
            <IconButton
              size="small"
              tabIndex={-1}
              title="Description suggestions"
              onMouseDown={(e) => {
                e.preventDefault();
                // Driven off what is on screen, not off a flipped flag: focusing the field
                // below clears the dismissal, so a blind toggle would fight its own focus
                // handler whenever the field was not already focused.
                setDismissed(open);
                setBulbOpen(!open);
                // Otherwise a click that shuts a list opened by typing would leave it free to reappear.
                setTyping(false);
                setFocused(true);
                inputRef.current?.focus();
              }}
              sx={{
                color: lit ? theme.palette.info.main : theme.palette.text.disabled,
                // typeToOpen: hovering must not light it — lit means "open" there.
                '&:hover': { color: typeToOpen ? undefined : theme.palette.info.main },
              }}
            >
              <LightbulbIcon fontSize="small" />
            </IconButton>
          ) : InputProps?.endAdornment,
        }}
      />
      {open && (
        <Box
          id={listId}
          role="listbox"
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {/* The bulb can hold the list open with nothing matching; say so rather than show a lit bulb over nothing. */}
          {!hasMatches && (
            <Typography
              variant="caption"
              sx={{ px: 1.5, py: 1, display: 'block', color: 'text.disabled' }}
            >
              No matching suggestions
            </Typography>
          )}
          {sections.map((section) => (
            <React.Fragment key={section.caption}>
              <Typography
                variant="caption"
                sx={{ px: 1.5, pt: 1, pb: 0.25, display: 'block', color: 'text.disabled' }}
              >
                {section.caption}
              </Typography>
              {section.items.map((desc) => {
                rowIndex += 1;
                const index = rowIndex;
                const active = index === activeIndex;
                return (
                  <Box
                    key={`${section.caption}:${desc}`}
                    id={optionId(index)}
                    role="option"
                    aria-selected={active}
                    ref={active ? activeRowRef : null}
                    // Move, not enter: a row scrolling under a resting mouse must not steal the highlight.
                    onMouseMove={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickOption(desc);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      bgcolor: active ? 'action.selected' : undefined,
                    }}
                  >
                    {desc}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DescriptionSuggestField;
